type Position = {
    right: number | null;
    bottom: number | null;
};

type DragControllerOptions = {
    getShadowRoot: () => ShadowRoot | null;
    getCurrentPosition: () => Position;
    setCurrentPosition: (right: number | null, bottom: number | null) => void;
};

export class WidgetDragController {
    private hasDrag = false;
    private limitRight = 0;
    private limitBottom = 0;
    private initialRight: number | null = null;
    private initialBottom: number | null = null;
    private dragObserver: MutationObserver | null = null;
    private dragTarget: HTMLButtonElement | null = null;
    private touchStartX = 0;
    private touchStartY = 0;
    private touchBeginRight = 0;
    private touchBeginBottom = 0;

    constructor(private readonly options: DragControllerOptions) { }

    public start() {
        this.addDrag();
    }

    public stop() {
        this.dragObserver?.disconnect();
        this.dragObserver = null;
        this.unbindTouchEvents();
    }

    public setDragLimits(right: number, bottom: number) {
        this.limitRight = this.sanitizeLimit(right);
        this.limitBottom = this.sanitizeLimit(bottom);
        this.hasDrag = true;
        this.addDrag();
    }

    public removeDrag() {
        this.limitBottom = 0;
        this.limitRight = 0;
        this.hasDrag = false;
        this.resetIconPosition();
        this.addDrag();
    }

    public get shouldApplyPosition() {
        const hasCapturedInitialPosition = this.initialRight !== null && this.initialBottom !== null;
        return (this.hasDrag && hasCapturedInitialPosition) || this.hasCustomPosition;
    }

    public get currentRight() {
        return this.getCurrentRight();
    }

    public get currentBottom() {
        return this.getCurrentBottom();
    }

    private sanitizeLimit(value: number) {
        return Number.isFinite(value) && value > 0 ? Math.floor(value) : 0;
    }

    private addDrag() {
        const shadowRoot = this.options.getShadowRoot();

        if (!this.dragObserver && shadowRoot) {
            this.dragObserver = new MutationObserver(() => {
                this.bindDragTarget();
            });

            this.dragObserver.observe(shadowRoot, { childList: true, subtree: true });
        }

        this.bindDragTarget();
    }

    private bindDragTarget() {
        const shadowRoot = this.options.getShadowRoot();
        const widget = shadowRoot?.querySelector<HTMLButtonElement>('#mariana-chat-toggle');

        if (!widget) {
            this.unbindTouchEvents();
            this.dragTarget = null;
            return;
        }

        if (this.dragTarget !== widget) {
            this.unbindTouchEvents();
            this.dragTarget = widget;
            this.captureInitialPosition(widget);
        }

        if (this.hasDrag) {
            this.bindTouchEvents();
        } else {
            this.unbindTouchEvents();
            widget.style.transition = '';
        }
    }

    private bindTouchEvents() {
        if (!this.dragTarget) {
            return;
        }

        this.dragTarget.removeEventListener('touchstart', this.onTouchStart);
        this.dragTarget.removeEventListener('touchmove', this.onTouchMove);
        this.dragTarget.removeEventListener('touchend', this.onTouchEnd);

        this.dragTarget.addEventListener('touchstart', this.onTouchStart, { passive: true });
        this.dragTarget.addEventListener('touchmove', this.onTouchMove, { passive: false });
        this.dragTarget.addEventListener('touchend', this.onTouchEnd, { passive: true });
    }

    private unbindTouchEvents() {
        if (!this.dragTarget) {
            return;
        }

        this.dragTarget.removeEventListener('touchstart', this.onTouchStart);
        this.dragTarget.removeEventListener('touchmove', this.onTouchMove);
        this.dragTarget.removeEventListener('touchend', this.onTouchEnd);
    }

    private readonly onTouchStart = (event: TouchEvent) => {
        if (!this.hasDrag || !this.dragTarget) {
            return;
        }

        const touch = event.touches[0];
        if (!touch) {
            return;
        }

        this.touchStartX = touch.clientX;
        this.touchStartY = touch.clientY;
        this.touchBeginRight = this.getCurrentRight();
        this.touchBeginBottom = this.getCurrentBottom();
        this.dragTarget.style.transition = 'none';
    };

    private readonly onTouchMove = (event: TouchEvent) => {
        if (!this.hasDrag || !this.dragTarget) {
            return;
        }

        const touch = event.touches[0];
        if (!touch) {
            return;
        }

        event.preventDefault();

        const deltaX = touch.clientX - this.touchStartX;
        const deltaY = touch.clientY - this.touchStartY;

        let newRight = this.touchBeginRight - deltaX;
        let newBottom = this.touchBeginBottom - deltaY;

        const rightInitial = this.getInitialRight();
        const bottomInitial = this.getInitialBottom();

        newRight = Math.max(rightInitial - this.limitRight, Math.min(rightInitial + this.limitRight, newRight));
        newBottom = Math.max(bottomInitial, Math.min(bottomInitial + this.limitBottom, newBottom));

        this.options.setCurrentPosition(newRight, newBottom);
    };

    private readonly onTouchEnd = () => {
        if (this.dragTarget) {
            this.dragTarget.style.transition = '';
        }
    };

    private captureInitialPosition(widget: HTMLButtonElement) {
        if (this.initialRight !== null && this.initialBottom !== null) {
            return;
        }

        const styles = getComputedStyle(widget);
        const parsedRight = Number.parseInt(styles.right, 10);
        const parsedBottom = Number.parseInt(styles.bottom, 10);
        this.initialRight = Number.isFinite(parsedRight) ? parsedRight : 0;
        this.initialBottom = Number.isFinite(parsedBottom) ? parsedBottom : 0;
        this.options.setCurrentPosition(this.initialRight, this.initialBottom);
    }

    private resetIconPosition() {
        this.options.setCurrentPosition(this.initialRight, this.initialBottom);
    }

    private getInitialRight() {
        return this.initialRight ?? 0;
    }

    private getInitialBottom() {
        return this.initialBottom ?? 0;
    }

    private getCurrentRight() {
        return this.options.getCurrentPosition().right ?? this.getInitialRight();
    }

    private getCurrentBottom() {
        return this.options.getCurrentPosition().bottom ?? this.getInitialBottom();
    }

    private get hasCustomPosition() {
        const position = this.options.getCurrentPosition();

        return position.right !== null
            && position.bottom !== null
            && position.right !== this.getInitialRight()
            && position.bottom !== this.getInitialBottom();
    }
}
